import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title: string
  description: string
  canonicalPath?: string
  ogType?: 'website' | 'article' | 'business.business'
  ogImage?: string
  noIndex?: boolean
}

const BASE_URL = 'https://theblackdogjiujitsu.com'
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`
const SITE_NAME = 'The Black Dog Jiu-Jitsu Company'

/**
 * SEO Component - Handles all meta tags for SEO optimization
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="A compelling description of the page content (150-160 chars ideal)"
 *   canonicalPath="/page-path"
 * />
 */
export default function SEO({
  title,
  description,
  canonicalPath = '',
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false
}: SEOProps) {
  const canonicalUrl = `${BASE_URL}${canonicalPath}`
  const fullTitle = `${title} | ${SITE_NAME}`

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO Tags */}
      <meta name="geo.region" content="US-OR" />
      <meta name="geo.placename" content="Lake Oswego" />
    </Helmet>
  )
}
